import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, KeyRound, PlayCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCourseApi, Course } from '../../hooks/useCourseApi';
import CourseCard from '../../components/course/CourseCard';
import CodeEntryForm from '../../components/common/CodeEntryForm';
import UnitCard from '../../components/unit/UnitCard';
import PortalPageHeader from '../../components/student-portal/PortalPageHeader';
import { unitCodeService } from '../../services/unitCodeService';
import { MyUnitCodeDto } from '../../types/unit';
import { getGradeInArabic } from '../../utils/gradeTranslations';

const PortalCourses: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { getCourses, fetchEnrolledCourseIds, error } = useCourseApi();
  const [courses, setCourses] = useState<Course[]>([]);
  const [enrolledCourseIds, setEnrolledCourseIds] = useState<string[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  const [enrolledUnits, setEnrolledUnits] = useState<MyUnitCodeDto[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(true);
  const [unitsError, setUnitsError] = useState('');
  const [unitsPage, setUnitsPage] = useState(1);
  const [unitsTotalPages, setUnitsTotalPages] = useState(1);
  const [unitsTotalCount, setUnitsTotalCount] = useState(0);

  const grade = currentUser?.grade || 'Secondary1';
  const gradeTitle = getGradeInArabic(grade);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const [coursesResult, enrolledIds] = await Promise.all([
          getCourses(grade, pageNumber, 10),
          fetchEnrolledCourseIds(),
        ]);

        if (cancelled) return;

        if (coursesResult?.success && coursesResult?.data) {
          setCourses(coursesResult.data.courses || []);
          setTotalPages(coursesResult.data.totalPages || 1);
        } else {
          setCourses([]);
          setTotalPages(1);
        }
        setEnrolledCourseIds(enrolledIds || []);
      } catch {
        if (!cancelled) {
          setCourses([]);
          setTotalPages(1);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [grade, pageNumber, getCourses, fetchEnrolledCourseIds]);

  const loadEnrolledUnits = useCallback(async (page = 1) => {
    setUnitsLoading(true);
    setUnitsError('');
    try {
      const response = await unitCodeService.getMyUnitCodes(page, 8);
      if (response.success && response.data) {
        setEnrolledUnits(response.data.units || []);
        setUnitsTotalPages(response.data.totalPages || 1);
        setUnitsTotalCount(response.data.totalCount || 0);
        setUnitsPage(response.data.currentPage || page);
      } else {
        const message = String(response.message || '');
        const isEmpty = /no enrolled units found/i.test(message);

        setEnrolledUnits([]);
        setUnitsTotalPages(1);
        setUnitsTotalCount(0);
        setUnitsPage(page);
        setUnitsError(isEmpty ? '' : 'تعذر تحميل الحصص المسجّلة');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      const isEmpty = /no enrolled units found/i.test(message);
      setEnrolledUnits([]);
      setUnitsError(isEmpty ? '' : message || 'تعذر تحميل الحصص المسجّلة');
    } finally {
      setUnitsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEnrolledUnits(1);
  }, [loadEnrolledUnits]);

  return (
    <div className="px-4 pb-8 pt-5 sm:px-6 sm:pt-7 lg:px-8" dir="rtl">
      <div className="mb-6 rounded-2xl border-2 border-teal-100 bg-white px-4 py-5 sm:mb-8 sm:px-6 sm:py-6">
        <PortalPageHeader
          eyebrow="الكورسات"
          title={`كورسات ${gradeTitle}`}
          description="كورسات صفك، وحصصك المسجّلة، وكود المحاضرة المستقلة"
          icon={<BookOpen className="h-4 w-4" />}
        />
      </div>

      <section className="mb-6 rounded-2xl border-2 border-teal-100 bg-white p-4 sm:mb-8 sm:p-6">
        <h2 className="mb-4 text-lg font-extrabold text-slate-900">كورسات الصف</h2>

        {isLoading && (
          <div className="flex min-h-[240px] items-center justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-teal-100 border-t-teal-600" />
          </div>
        )}

        {!isLoading && error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center text-red-700">
            {error}
          </div>
        )}

        {!isLoading && !error && courses.length === 0 && (
          <div className="rounded-xl border-2 border-teal-100 bg-white/70 py-12 text-center">
            <BookOpen className="mx-auto mb-3 h-12 w-12 text-teal-300" />
            <p className="text-slate-500">لا توجد كورسات متاحة لـ{gradeTitle} حالياً</p>
          </div>
        )}

        {!isLoading && !error && courses.length > 0 && (
          <>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {courses.map((course) => (
                <CourseCard
                  key={course.id}
                  course={course}
                  isEnrolled={enrolledCourseIds.includes(course.id)}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-8 flex justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setPageNumber((p) => Math.max(p - 1, 1))}
                  disabled={pageNumber === 1}
                  className="rounded-xl border-2 border-teal-100 bg-white px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-40"
                >
                  السابق
                </button>
                <span className="rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white">
                  {pageNumber} من {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPageNumber((p) => Math.min(p + 1, totalPages))}
                  disabled={pageNumber === totalPages}
                  className="rounded-xl border-2 border-teal-100 bg-white px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-40"
                >
                  التالي
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <section className="mb-6 rounded-2xl border-2 border-teal-100 bg-white p-4 sm:mb-8 sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <PlayCircle className="h-5 w-5 text-slate-900" />
            <h2 className="text-lg font-extrabold text-slate-900">حصصي المسجّلة</h2>
          </div>
          {!unitsLoading && unitsTotalCount > 0 && (
            <p className="text-sm text-slate-500">
              {unitsTotalCount} {unitsTotalCount === 1 ? 'حصة' : 'حصص'}
            </p>
          )}
        </div>

        {unitsLoading && (
          <div className="flex min-h-[160px] items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-teal-100 border-t-teal-600" />
          </div>
        )}

        {!unitsLoading && unitsError && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center text-sm text-red-700">
            {unitsError}
            <button
              type="button"
              onClick={() => loadEnrolledUnits(unitsPage)}
              className="mt-2 block w-full font-semibold text-teal-600 hover:text-teal-700"
            >
              إعادة المحاولة
            </button>
          </div>
        )}

        {!unitsLoading && !unitsError && enrolledUnits.length === 0 && (
          <div className="rounded-xl border border-teal-100 bg-white/70 py-10 text-center">
            <PlayCircle className="mx-auto mb-3 h-10 w-10 text-teal-300" />
            <p className="text-sm font-medium text-slate-600">مفيش حصص مسجّلة دلوقتي</p>
            <p className="mt-1 text-sm text-slate-500">ادخل كود المحاضرة من تحت عشان تتفتحلك</p>
          </div>
        )}

        {!unitsLoading && !unitsError && enrolledUnits.length > 0 && (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {enrolledUnits.map((unit) => (
                <div
                  key={unit.unitId}
                  onClick={() => navigate(`/unit-viewer/${unit.unitId}`)}
                  className="cursor-pointer"
                  role="presentation"
                >
                  <UnitCard unit={unit} />
                </div>
              ))}
            </div>

            {unitsTotalPages > 1 && (
              <div className="mt-6 flex justify-center gap-2">
                <button
                  type="button"
                  onClick={() => loadEnrolledUnits(Math.max(unitsPage - 1, 1))}
                  disabled={unitsPage === 1}
                  className="rounded-xl border-2 border-teal-100 bg-white px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-40"
                >
                  السابق
                </button>
                <span className="rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white">
                  {unitsPage} من {unitsTotalPages}
                </span>
                <button
                  type="button"
                  onClick={() => loadEnrolledUnits(Math.min(unitsPage + 1, unitsTotalPages))}
                  disabled={unitsPage === unitsTotalPages}
                  className="rounded-xl border-2 border-teal-100 bg-white px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-40"
                >
                  التالي
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <section className="rounded-2xl border-2 border-teal-100 bg-white p-4 sm:p-6">
        <div className="mb-2 flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-slate-900" />
          <h2 className="text-lg font-extrabold text-slate-900">
            أكواد محاضرات السنتر
          </h2>
        </div>
        <p className="mb-4 text-sm text-slate-600">أدخل كود الحصة عشان تتفتحلك مباشرة</p>
        <CodeEntryForm
          compact
          redirectTo="/student-portal"
          onSuccess={() => loadEnrolledUnits(1)}
        />
      </section>
    </div>
  );
};

export default PortalCourses;
